import { useEffect, useState, useMemo, useCallback } from 'react';

/**
 * Generic hook for fetching report data based on params.
 * Refetches when params change (compared by JSON.stringify).
 *
 * Use for new report APIs: pass params and a fetch function. Prefer a stable fetchFn
 * (module-level or useCallback) to avoid unnecessary refetches.
 *
 * @example
 * // In a provider:
 * const fetchParams = useMemo(() => ({ level, levelId, period, ... }), [...]);
 * const { data, loading, error } = useReportData(fetchParams, Reports.myNewReport, { logLabel: 'my report' });
 *
 * @example
 * // Standalone in a component:
 * const { data, loading, error, refetch } = useReportData(params, (p) => Api.fetch(p).then(r => r?.data ?? r));
 *
 * @template T
 * @param {Object} params - Params object passed to fetchFn (e.g. level, levelId, period, term, year)
 * @param {(params: Object) => Promise<T>} fetchFn - Async function that fetches data
 * @param {Object} [opts] - Options
 * @param {string} [opts.logLabel] - Label for console.error on failure
 * @returns {{ data: T|null, loading: boolean, error: Error|null, refetch: () => void }}
 */
function useReportData(params, fetchFn, opts = {}) {
    const { logLabel = 'report' } = opts;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const paramsKey = useMemo(() => JSON.stringify(params), [params]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchFn(params)
            .then((result) => {
                if (!cancelled && result != null) {
                    setData(result);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err);
                    if (logLabel) {
                        console.error(`Error fetching ${logLabel}:`, err);
                    }
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [paramsKey, refreshTrigger, fetchFn]);

    const refetch = useCallback(() => {
        setRefreshTrigger((t) => t + 1);
    }, []);

    return { data, loading, error, refetch };
}

export default useReportData;
